import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Sidebar, FootNav, Select, useTheme } from '@dreamtree-org/twreact-ui';
import {
  LayoutDashboard, Receipt, Package, Users, Settings, Shield, KeySquare, Moon, Sun, Circle, MessageCircle, Bot, Code, Wrench, Volume2, Plug, Database, PlugZap, ShieldCheck, PhoneCall, GitBranch, AppWindow, ServerCog, Server, MonitorCheck, ScanEye, Puzzle,
} from 'lucide-react';
import { useAppApi } from '@/lib/api';
import navbarEmitter from '@/lib/emitter';
import { useAuth, useCan } from '@rbac/useCan';
import { clearAuth } from '@rbac/permissions';
import { buildRoutes } from '@/routes';
import { useModules } from '@/modules';
import { groupIntoSections } from '@/lib/navSections';
import { ChatDockProvider, useChatDock } from '@/lib/ChatDockContext';
import { CompanyProvider, useCompany } from '@/lib/CompanyContext';
import { switchBranch, tenantLogout } from '@/lib/tenantAuth';
import AgentChatDock from '@/components/AgentChatDock';

const ICONS = {
  'layout-dashboard': LayoutDashboard, receipt: Receipt, package: Package, users: Users,
  settings: Settings, shield: Shield, 'key-square': KeySquare,
  'message-circle': MessageCircle, bot: Bot, code: Code, wrench: Wrench, 'volume-2': Volume2,
  plug: Plug, database: Database,
  // Every name modules.config.js declares must appear here: an unmapped one falls through to
  // the generic Circle below, and five sidebar entries were rendering that same blank icon.
  'plug-zap': PlugZap, 'shield-check': ShieldCheck, 'phone-call': PhoneCall,
  'git-branch': GitBranch, 'app-window': AppWindow, 'server-cog': ServerCog, server: Server,
  'monitor-check': MonitorCheck,
  // 'scan-eye' (uiaudits) and 'puzzle' (connectors) were declared in modules.config.js but
  // never mapped here, so both sidebar entries had been rendering the blank Circle
  // fallback — the exact drift the note above warns about, found again while adding
  // 'monitor-check'.
  'scan-eye': ScanEye, puzzle: Puzzle,
};
const Icon = ({ name, size = 18 }) => {
  const C = ICONS[name] || Circle;
  return <C size={size} />;
};

/**
 * Responsive app shell (all screen sizes):
 *   - Navbar across the top
 *   - Sidebar on >= md (laptop/desktop/ultrawide), hidden on phones
 *   - FootNav (twreact, mobile-only by design) on phones
 * Nav items are filtered by the user's (module, action='view') permissions.
 */
export default function DefaultLayout() {
  return (
    <CompanyProvider>
      <ChatDockProvider>
        <DefaultLayoutInner />
      </ChatDockProvider>
    </CompanyProvider>
  );
}

/**
 * The Navbar's right-hand cluster. MUST be a component, not an element: twreact resolves
 * `rightMenuContent` with `typeof prop === 'function' ? prop : <default/>`, so passing JSX
 * silently falls through to the built-in notifications bell — which is exactly why the
 * theme toggle below never appeared before.
 *
 * Note this REPLACES the bell. Nothing in this app ever subscribed to navbarEmitter, so
 * no functionality is lost.
 */
/** Multi-tenant mode: the active branch, switchable server-side (the session rotates). */
function BranchSwitcher({ user }) {
  const branches = (user && user.branches) || [];
  if (!branches.length) return null;
  const active = branches.find((b) => b.id === user.activeBranchId);
  if (branches.length === 1) {
    return <span className="text-sm text-secondary-600 px-2" title="Active branch">{active ? active.name : ''}</span>;
  }
  return (
    <div className="w-48 sm:w-56">
      <Select
        aria-label="Active branch"
        options={branches.map((b) => ({ value: b.id, label: `${b.code} — ${b.name}` }))}
        value={user.activeBranchId || ''}
        onChange={(v) => { if (v && v !== user.activeBranchId) switchBranch(v); }}
        allowClear={false}
        closeOnSelect
      />
    </div>
  );
}

function NavRight({ companies, companyId, setCompany, toggleTheme, isDark, user }) {
  return (
    <div className="flex items-center gap-2">
      {user && user.mode === 'multi' && <BranchSwitcher user={user} />}
      {/* Hidden for a single-company user: a one-option switcher is noise. */}
      {(!user || user.mode !== 'multi') && companies.length > 1 && (
        <div className="w-48 sm:w-56">
          <Select
            options={companies.map((c) => ({ value: String(c.id), label: c.name }))}
            value={companyId == null ? '' : String(companyId)}
            onChange={setCompany}
            placeholder="Company"
            searchable
            allowClear={false}
            closeOnSelect
          />
        </div>
      )}
      <button onClick={toggleTheme} className="p-2 rounded-md hover:bg-[var(--app-border)]/30" aria-label="Toggle theme">
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>
    </div>
  );
}

function DefaultLayoutInner() {
  const nav = useNavigate();
  const loc = useLocation();
  const user = useAuth();
  const can = useCan();
  const { toggleTheme, isDark } = useTheme();
  const { fetchApi } = useAppApi();
  const { openRequest, getScreenContext, publishProposal } = useChatDock();
  const { companyId, companies, setCompany } = useCompany();
  const [collapsed, setCollapsed] = useState(false);

  const { modules } = useModules();
  const allowed = buildRoutes(modules).filter((r) => can(r.module, r.action));

  const toItem = (r) => ({
    id: r.path,
    label: r.label,
    icon: <Icon name={r.icon} />,
    active: loc.pathname === r.path,
    onClick: () => nav(r.path),
  });
  // Multi-page modules collapse into one sidebar group (twreact Sidebar `children`) —
  // e.g. Connections / Connection Categories / Connection Providers under "Connections".
  // Sections are the same mechanism one level up: each section is a parent item whose
  // children are that section's pages and module groups, so sections collapse with the
  // library's own chevron behaviour instead of custom header components. One Sidebar
  // stays one Sidebar — it renders its own toggle header and scroll container, so a
  // Sidebar-per-section would duplicate both.
  const nestGroups = (routes) => {
    const items = [];
    const groups = new Map();
    for (const r of routes) {
      if (!r.group) {
        items.push(toItem(r));
        continue;
      }
      let parent = groups.get(r.group.key);
      if (!parent) {
        parent = { id: `group:${r.group.key}`, label: r.group.label, icon: <Icon name={r.group.icon} />, children: [] };
        groups.set(r.group.key, parent);
        items.push(parent);
      }
      parent.children.push(toItem(r));
    }
    return items;
  };
  const sidebarItems = groupIntoSections(allowed).map((section) => ({
    id: `section:${section.key}`,
    label: section.label,
    icon: <Icon name={section.icon} />,
    children: nestGroups(section.routes),
  }));
  const footItems = allowed.slice(0, 5).map(toItem);

  const logout = async () => {
    if (user && user.mode === 'multi') {
      await tenantLogout().catch(() => {});
    } else {
      try { await fetchApi('logout'); } catch {}
      clearAuth();
    }
    nav('/login', { replace: true });
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Navbar
        emitter={navbarEmitter}
        items={[]}
        logo={<span className="font-bold text-primary-600 px-2">AI Agent Builder</span>}
        user={user ? { name: user.name, email: user.email } : undefined}
        rightMenuContent={() => (
          <NavRight
            companies={companies} companyId={companyId} setCompany={setCompany}
            toggleTheme={toggleTheme} isDark={isDark} user={user}
          />
        )}
        onLogoutClick={logout}
        onLeftIconClick={() => setCollapsed((c) => !c)}
      />

      <div className="flex flex-1 min-h-0">
        {/* Sidebar: tablet and up */}
        <aside data-testid="sidebar" className="hidden md:block shrink-0 overflow-y-auto border-r border-[var(--app-border)] bg-[var(--app-surface)]">
          <Sidebar items={sidebarItems} collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
        </aside>

        {/* Height-bounded flex column; gutters, the ultrawide cap and the scroll
            container belong to PageShell so a canvas screen can opt out of all three. */}
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden pt-safe">
          {/* key on companyId remounts the routed page when the company changes, so every
              page refetches without each one having to know the company exists. React
              Router mounts exactly one route element at a time, so this is cheap — and it
              stays correct for pages added later. Page state (filters, scroll) is
              intentionally discarded: it belongs to the company you just left. */}
          <div key={companyId ?? 'default'} className="flex-1 min-h-0 flex flex-col">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom tab bar: phones only (twreact FootNav is md:hidden) */}
      <FootNav items={footItems} className="pb-safe" data-testid="footnav" />

      {/* Floating chat dock (PRD §3.8) — gated the same as the /chat page/nav item */}
      {can('agents', 'chat') && (
        <AgentChatDock
          openRequest={openRequest}
          getScreenContext={getScreenContext}
          publishProposal={publishProposal}
        />
      )}
    </div>
  );
}
